import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const LEAVE_TYPE_LABEL = {
  earned: "Earned Leave",
  casual: "Casual Leave",
  medical: "Medical Leave",
};

const STATUS_BADGE = {
  pending: "bg-warning",
  approved: "bg-success",
  rejected: "bg-danger",
};

const LeaveRegister = () => {
  // Locked to the current year — quotas and usage are year-scoped server-side.
  const year = String(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kitchens, setKitchens] = useState([]);

  // Form state — what the user is currently typing / selecting.
  const [searchInput, setSearchInput] = useState("");
  const [kitchenInput, setKitchenInput] = useState("");
  const [fromDateInput, setFromDateInput] = useState("");
  const [toDateInput, setToDateInput] = useState("");

  // Applied state — what's actually being filtered. Only updates when the
  // Search button is clicked, so the table doesn't flicker while typing.
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedKitchen, setAppliedKitchen] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  // Modal state — `detail` and `summary` share these.
  const [modalMode, setModalMode] = useState(null); // "detail" | "summary" | null
  const [modalEmp, setModalEmp] = useState(null);

  const fetchRegister = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://ims-attendanceback.indevconsultancy.in/api/leave-register/?year=${year}`
      );
      if (res.data.status) {
        setRows(res.data.data || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      console.error("Leave register fetch failed:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchKitchens = async () => {
    try {
      const res = await axios.get("https://ims-attendanceback.indevconsultancy.in/api/kitchen-list");
      setKitchens(res.data.kitchens || []);
    } catch (e) {
      console.error("Kitchen list fetch failed:", e);
    }
  };

  useEffect(() => {
    fetchRegister();
    fetchKitchens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    setAppliedSearch(searchInput);
    setAppliedKitchen(kitchenInput);
    setAppliedFromDate(fromDateInput);
    setAppliedToDate(toDateInput);
  };

  const handleReset = () => {
    setSearchInput("");
    setKitchenInput("");
    setFromDateInput("");
    setToDateInput("");
    setAppliedSearch("");
    setAppliedKitchen("");
    setAppliedFromDate("");
    setAppliedToDate("")
  };

  // A leave row overlaps the date filter if its [adjustment_date, to_date]
  // intersects with [appliedFromDate, appliedToDate]. Dates are YYYY-MM-DD
  // strings so simple string compare works.
  const leaveInDateRange = (l) => {
    if (!appliedFromDate && !appliedToDate) return true;
    if (appliedFromDate && l.to_date < appliedFromDate) return false;
    if (appliedToDate && l.adjustment_date > appliedToDate) return false;
    return true;
  };

  const filteredRows = rows.filter((r) => {
    // Kitchen filter (exact match on kitchen_name from dropdown).
    if (appliedKitchen && r.kitchen_name !== appliedKitchen) return false;
    // Free-text search across name / code / kitchen.
    if (appliedSearch.trim()) {
      const q = appliedSearch.trim().toLowerCase();
      const matches =
        (r.emp_name || "").toLowerCase().includes(q) ||
        (r.employee_code || "").toLowerCase().includes(q) ||
        (r.kitchen_name || "").toLowerCase().includes(q);
      if (!matches) return false;
    }
    // Only employees who have applied for at least one leave.
    if (!r.leaves || r.leaves.length === 0) return false;
    // Date-range filter — keep employee if any leave overlaps the picked range.
    if (appliedFromDate || appliedToDate) {
      const hasInRange = r.leaves.some(leaveInDateRange);
      if (!hasInRange) return false;
    }
    return true;
  });

  const openDetail = (emp) => {
    setModalEmp(emp);
    setModalMode("detail");
  };
  const openSummary = (emp) => {
    setModalEmp(emp);
    setModalMode("summary");
  };
  const closeModal = () => {
    setModalMode(null);
    setModalEmp(null);
  };

  const handleDownloadExcel = () => {
    if (!filteredRows.length) return;

    // Summary sheet — one row per employee.
    const summaryRows = filteredRows.map((r, i) => ({
      "S.No": i + 1,
      "Emp Code": r.employee_code || "",
      "Name": r.emp_name || "",
      "Kitchen": r.kitchen_name || "",
      "Total EL": r.el_quota,
      "EL Used": r.el_used,
      "EL Balance": r.el_quota - r.el_used,
      "Total CL": r.cl_quota,
      "CL Used": r.cl_used,
      "CL Balance": r.cl_quota - r.cl_used,
      // ML columns hidden for now — uncomment when medical leave is re-enabled.
      // "Total ML": r.ml_quota,
      // "ML Used": r.ml_used,
      // "ML Balance": r.ml_quota - r.ml_used,
      "Total Used": r.total_used,
      "Total Balance": r.total_balance,
    }));

    // Detail sheet — one row per individual leave record (across all employees).
    // If a date range is applied, only the leaves overlapping that range are
    // included so the Excel matches the on-screen filter.
    const detailRows = [];
    filteredRows.forEach((r) => {
      let leavesForExport = r.leaves || [];
      if (appliedFromDate || appliedToDate) {
        leavesForExport = leavesForExport.filter(leaveInDateRange);
      }
      leavesForExport.forEach((l, idx) => {
        // Skip ML rows in the detail sheet too while medical leave is hidden.
        if (l.leave_type === "medical") return;
        detailRows.push({
          "Emp Code": r.employee_code || "",
          "Name": r.emp_name || "",
          "Kitchen": r.kitchen_name || "",
          "Leave #": idx + 1,
          "Type": l.leave_type === "earned" ? "Earned Leave" : "Casual Leave",
          "From Date": fmtDate(l.adjustment_date),
          "To Date": fmtDate(l.to_date),
          "Days": l.days,
          "Status": l.approval_status,
          "Reason": l.reason || "",
        });
      });
    });

    // Heading rows at the top of each sheet — kis date se kis date tak ka
    // data hai. Bina filter ke poora saal (01 Jan – 31 Dec) dikhta hai.
    const headFrom = appliedFromDate || `${year}-01-01`;
    const headTo = appliedToDate || `${year}-12-31`;
    const rangeText = `From: ${fmtDate(headFrom)}  To: ${fmtDate(headTo)}`;
    const headingRows = [
      ["Leave Register" + (appliedKitchen ? ` — ${appliedKitchen}` : "")],
      [rangeText],
      [], // blank spacer row before the table
    ];

    const wb = XLSX.utils.book_new();
    const wsSummary = XLSX.utils.aoa_to_sheet(headingRows);
    XLSX.utils.sheet_add_json(wsSummary, summaryRows, { origin: "A4" });
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
    if (detailRows.length) {
      const wsDetail = XLSX.utils.aoa_to_sheet(headingRows);
      XLSX.utils.sheet_add_json(wsDetail, detailRows, { origin: "A4" });
      XLSX.utils.book_append_sheet(wb, wsDetail, "Leave Details");
    }

    const parts = ["Leave_Register"];
    if (appliedKitchen) parts.push(appliedKitchen.replace(/\s+/g, "_"));
    if (appliedFromDate && appliedToDate) {
      parts.push(`${appliedFromDate}_to_${appliedToDate}`);
    } else {
      parts.push(year);
    }
    XLSX.writeFile(wb, `${parts.join("_")}.xlsx`);
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB");
  };

  return (
    <div className="px-3 py-3 bg-cream" style={{ minHeight: "100vh" }}>
      <div className="card">
        <div className="card-header bg-skyBlue text-white">
          <h5 className="mb-0">Leave Register</h5>
        </div>
        <div className="card-body">
          {/* Filters */}
          <div className="row g-2 align-items-end mb-2">
            <div className="col-md-2">
              <label className="form-label small mb-1">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDateInput}
                onChange={(e) => setFromDateInput(e.target.value)}
                max={toDateInput || undefined}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDateInput}
                onChange={(e) => setToDateInput(e.target.value)}
                min={fromDateInput || undefined}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Kitchen</label>
              <select
                className="form-select"
                value={kitchenInput}
                onChange={(e) => setKitchenInput(e.target.value)}
              >
                <option value="">-- All Kitchens --</option>
                {kitchens.map((k) => (
                  <option key={k.kitchenid} value={k.kitchen_name}>
                    {k.kitchen_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">
                Search (Name / Code)
              </label>
              <input
                type="text"
                className="form-control"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchClick();
                }}
                placeholder="Type and press Search…"
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={handleSearchClick}
                disabled={loading}
              >
                Search
              </button>
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-secondary w-100"
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </div>

         <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-success btn-sm"
              onClick={handleDownloadExcel}
              disabled={loading || filteredRows.length === 0}
              title="Download visible rows as Excel"
            >
             Download Excel
            </button>
            <small className="text-muted">
              Total Employee: <strong>{filteredRows.length}</strong>
              {appliedKitchen && (
                <>
                  {" "}
                  · Kitchen: <strong>{appliedKitchen}</strong>
                </>
              )}
              {(appliedFromDate || appliedToDate) && (
                <>
                  {" "}
                  · Range: <strong>{appliedFromDate || "…"}</strong> to{" "}
                  <strong>{appliedToDate || "…"}</strong>
                </>
              )}
            </small>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-bordered table-striped align-middle">
              <thead className="table-light text-center">
                <tr>
                  <th>S.No</th>
                  <th>Emp Code</th>
                  <th>Name</th>
                  <th>Kitchen</th>
                  <th>EL</th>
                  <th>CL</th>
                   {/* <th>ML</th>  // hidden for now */}
                  <th>Used</th>
                  <th>Balance</th>
                  <th>Detail</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      Loading…
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      No employees found for {year}.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, i) => (
                    <tr key={r.empdetailsid}>
                      <td className="text-center">{i + 1}</td>
                      <td>{r.employee_code || "-"}</td>
                      <td>{r.emp_name}</td>
                      <td>{r.kitchen_name || "-"}</td>
                      <td className="text-center">{r.el_quota}</td>
                      <td className="text-center">{r.cl_quota}</td>
                      {/* <td className="text-center">{r.ml_quota}</td>  // hidden for now */}
                      <td className="text-center">
                        <span className="badge bg-warning text-dark">
                          {r.total_used}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${r.total_balance > 0 ? "bg-success" : "bg-secondary"
                            }`}
                        >
                          {r.total_balance}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openDetail(r)}
                        >
                          View
                        </button>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => openSummary(r)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer text-muted text-center small">
          Technology Partner: Indev Consultancy ©2025
        </div>
      </div>

      {/* Detail Modal — kis-kis din leave li hai */}
      {modalMode === "detail" && modalEmp && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Leave Detail — {modalEmp.emp_name}{" "}
                  <small className="text-muted">({modalEmp.employee_code})</small>
                </h5>
                <button className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                {modalEmp.leaves.length === 0 ? (
                  <div className="alert alert-secondary mb-0">
                    No leave records this year.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Type</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Days</th>
                          <th>Status</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalEmp.leaves.map((l, i) => (
                          <tr key={l.id}>
                            <td>{i + 1}</td>
                            <td>
                              {LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type}
                            </td>
                            <td>{fmtDate(l.adjustment_date)}</td>
                            <td>{fmtDate(l.to_date)}</td>
                            <td>{l.days}</td>
                            <td>
                              <span
                                className={`badge ${STATUS_BADGE[l.approval_status] || "bg-secondary"
                                  }`}
                              >
                                {l.approval_status}
                              </span>
                            </td>
                            <td>{l.reason || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal — per-type breakdown */}
      {modalMode === "summary" && modalEmp && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Leave Summary — {modalEmp.emp_name}{" "}
                  <small className="text-muted">({modalEmp.employee_code})</small>
                </h5>
                <button className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-2">
                  Year: <strong>{year}</strong>. Used counts include both
                  approved &amp; pending requests.
                </p>
                <table className="table table-bordered text-center mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Type</th>
                      <th>Quota</th>
                      <th>Used</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Earned Leave (EL)</td>
                      <td>{modalEmp.el_quota}</td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {modalEmp.el_used}
                        </span>
                      </td>
                      <td>{modalEmp.el_quota - modalEmp.el_used}</td>
                    </tr>
                    <tr>
                      <td>Casual Leave (CL)</td>
                      <td>{modalEmp.cl_quota}</td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {modalEmp.cl_used}
                        </span>
                      </td>
                      <td>{modalEmp.cl_quota - modalEmp.cl_used}</td>
                    </tr>
                    {/* Medical Leave row — hidden for now, unhide later */}
                    {/* <tr>
                      <td>Medical Leave (ML)</td>
                      <td>{modalEmp.ml_quota}</td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {modalEmp.ml_used}
                        </span>
                      </td>
                      <td>{modalEmp.ml_quota - modalEmp.ml_used}</td>
                    </tr> */}
                    <tr className="fw-bold table-secondary">
                      <td>Total</td>
                      <td>{modalEmp.total_quota}</td>
                      <td>{modalEmp.total_used}</td>
                      <td>{modalEmp.total_balance}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRegister;
